import { Button, CustomerContactModal, FeedbackNotes, ResolveFeedbackModal, Tooltip } from '@app/components';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useAppSelector } from '@app/redux/hooks';
import GoogleLogo from '@app/assets/icons/Google.svg?react';
import FullStarIcon from '@app/assets/icons/star-full.svg?react';
import EmptyStarIcon from '@app/assets/icons/star-empty.svg?react';
import YelpIcon from '@app/assets/icons/integrations/yelp.svg?react';
import EchoLogo from '@app/assets/simple-logo.svg?react';
import { BusinessReviews } from '@app/models/reviews';
import { getFeedbackNotes, createFeedbackNote, updateFeedbackNote, deleteFeedbackNote } from '@app/services/reviews';
import { getTimeAgo } from '@app/utils/utils';

const isRecentReview = (date: Date) => {
  const oneDayAgo = new Date();
  oneDayAgo.setDate(oneDayAgo.getDate() - 1);
  return date > oneDayAgo;
};

export const FeedbackReviewBox = ({
  review,
  onResolve,
  isResolving = false,
}: {
  review: BusinessReviews & { platform?: string; badReasons?: Array<{ id: string; reason: string; businessReviewId: string; createdAt: string; updatedAt: string }> };
  onResolve?: (reviewId: string, resolved: boolean, data?: { sendFollowUp: boolean; notes?: string }) => Promise<{ success: boolean; error?: any }>;
  isResolving?: boolean;
}) => {
  const [isCustomerContactOpen, setIsCustomerContactOpen] = useState(false);
  const [isResolveModalOpen, setIsResolveModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const authState = useAppSelector((state) => state.auth);

  // Fetch notes for the current review
  const { data: notesData, isLoading: isLoadingNotes } = useQuery({
    queryKey: ['feedback-notes', review.id],
    queryFn: () => getFeedbackNotes(review.id),
    enabled: !!review.id,
  });

  const notes = notesData?.data || [];

  // Create note mutation
  const createNoteMutation = useMutation({
    mutationFn: (description: string) => createFeedbackNote(review.id, description),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback-notes', review.id] });
 
    },
    onError: (error) => {
      console.error('Error creating note:', error);
      toast.error('Failed to add note');
    },
  });

  // Update note mutation
  const updateNoteMutation = useMutation({
    mutationFn: ({ noteId, description }: { noteId: string; description: string }) => 
      updateFeedbackNote(noteId, description),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback-notes', review.id] });
      toast.success('Note updated successfully');
    },
    onError: (error) => {
      console.error('Error updating note:', error);
      toast.error('Failed to update note');
    },
  });

  // Delete note mutation
  const deleteNoteMutation = useMutation({
    mutationFn: (noteId: string) => deleteFeedbackNote(noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback-notes', review.id] });
      toast.success('Note deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting note:', error);
      toast.error('Failed to delete note');
    },
  });

  const handleAddNote = (description: string) => {
    createNoteMutation.mutate(description);
  };

  const handleEditNote = (noteId: string, description: string) => {
    updateNoteMutation.mutate({ noteId, description });
  };

  const handleDeleteNote = (noteId: string) => {
    deleteNoteMutation.mutate(noteId);
  };

  const handleResolveWithModal = async (data: { sendFollowUp: boolean; notes?: string }) => {
    if (onResolve && review?.id) {
      const result = await onResolve(review.id, true, data);
      if (result?.success) {
        setIsResolveModalOpen(false);
      }
    }
  };

  const handleResolveClick = () => {
    setIsResolveModalOpen(true);
  };

  // Safety check for undefined review
  if (!review) {
    return (
      <div className="bg-white p-4 rounded-lg space-y-4">
        <p className="text-sm text-base-coolGray">No feedback selected</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white p-3 sm:p-4 rounded-lg space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-2">
          <div className="flex items-center gap-2 flex-1">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-base-antiFlash flex items-center justify-center flex-shrink-0">
              {(review?.platform ?? 'internal') === 'yelp' ? (
                <YelpIcon width={24} height={24} className="sm:w-[30px] sm:h-[30px]" />
              ) : (review?.platform ?? 'internal') === 'internal' ? (
                <EchoLogo style={{ width: 24, height: 24 }} className="sm:w-[30px] sm:h-[30px]" />
              ) : (
                <GoogleLogo style={{ width: 24, height: 24 }} className="sm:w-[30px] sm:h-[30px]" />
              )}
            </div>
            <div className="space-y-1 min-w-0 flex-1">
              <div className="relative group">
                <h4 className="font-semibold text-sm sm:text-base truncate">
                  <Link to={`/customers/contacts/${review?.customer?.id}`} className='hover:underline'>
                    {(review?.customer?.firstName || 'Unknown')} {(review?.customer?.lastName || '')}
                  </Link>
                  {review?.createdAt && isRecentReview(new Date(review.createdAt)) && (
                    <span className="bg-system-successLight text-system-success p-1 text-xs rounded-sm ml-2">
                      New
                    </span>
                  )}
                </h4>
                <div className="absolute left-0 top-full mt-2 z-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-base-raisinBlack/90"></div>
                  <div className="bg-base-raisinBlack/90 text-white p-2 rounded-lg text-sm whitespace-nowrap normal-case">
                    Open profile
                  </div>
                </div>
              </div>

              <p className="text-xs sm:text-sm text-base-coolGray">
                Left a feedback {review?.createdAt ? getTimeAgo(new Date(review.createdAt)) : ''}
                {review?.photos?.length > 0 && ` | ${review.photos.length} photos`}
              </p>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            {!review?.resolved ? (
              <Tooltip content="Move to resolved" direction="down">
                <Button
                  type="button"
                  kind="secondary"
                  size="small"
                  disabled={isResolving}
                  onClick={handleResolveClick}
                  className="w-full sm:w-auto text-xs sm:text-sm"
                >
                  {isResolving ? 'Resolving...' : 'Resolve'}
                </Button>
              </Tooltip>
            ) : (
              <Button
                type="button"
                kind="secondary"
                size="small"
                disabled={isResolving}
                onClick={() => {
                  if (onResolve && review?.id) {
                    onResolve(review.id, false);
                  }
                }}
                className="w-full sm:w-auto text-xs sm:text-sm"
              >
                {isResolving ? 'Unresolving...' : 'Unresolve'}
              </Button>
            )}
            <Button
              type="button"
              size="small"
              onClick={() => setIsCustomerContactOpen(true)}
              className="w-full sm:w-auto text-xs sm:text-sm"
            >
              Customer contact
            </Button>
          </div>
        </div>
        
        <div className="flex items-center gap-1">
          {[...Array(5)].map((_, index) =>
            index < (review?.rating || 0) ? (
              <FullStarIcon key={index} width={18} height={18} className="sm:w-6 sm:h-6" />
            ) : (
              <EmptyStarIcon key={index} width={18} height={18} className="sm:w-6 sm:h-6" />
            )
          )}
        </div>
        {/* Bad Reasons Tags */}
        {review?.badReasons && review.badReasons.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {review.badReasons.map((badReason) => (
              <span
                key={badReason.id}
                className="inline-block bg-[#F6F7FA] text-gray-700 text-xs px-3 py-2 rounded font-medium"
              >
                {badReason.reason}
              </span>
            ))}
          </div>
        )}
        <div>
          <p className="text-sm sm:text-base leading-relaxed">{review?.review || ''}</p>
        </div>
        
        {review?.photos && review.photos.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 gap-2 max-w-md">
            {review.photos.map((photo) => (
              <Tooltip key={photo.id} content="Open" direction="up">
                <div className="w-full aspect-square relative rounded-[4px] overflow-hidden cursor-pointer">
                  <img
                    alt="review attached"
                    src={photo.url}
                    className="absolute w-full h-full object-cover object-center"
                  />
                </div>
              </Tooltip>
            ))}
          </div>
        )}
        
        {review?.reply && (
          <div className="bg-base-seasalt p-3 sm:p-4 border-t border-t-base-ghostWhite">
            <label className="block text-sm sm:text-base font-medium mb-2">Response</label>
            <textarea
              value={review.reply}
              readOnly
              className="w-full p-2 sm:p-3 border rounded bg-gray-100 text-gray-700 resize-none text-sm sm:text-base"
              rows={3}
            />
          </div>
        )}
        
        {/* Notes Section */}
        {notes.length > 0 && (
          <FeedbackNotes
            notes={notes}
            onAddNote={handleAddNote}
            onEditNote={handleEditNote}
            onDeleteNote={handleDeleteNote}
            isLoading={isLoadingNotes}
            isCreating={createNoteMutation.isPending}
            isUpdating={updateNoteMutation.isPending}
            isDeleting={deleteNoteMutation.isPending}
            currentUserName={`${authState?.user?.firstName || ''} ${authState?.user?.lastName || ''}`.trim() || 'Unknown User'}
          />
        )}
      </div>
      
      <CustomerContactModal
        isOpen={isCustomerContactOpen}
        onClose={() => setIsCustomerContactOpen(false)}
        customer={review?.customer}
      />
      
      <ResolveFeedbackModal
        isOpen={isResolveModalOpen}
        onClose={() => setIsResolveModalOpen(false)}
        onResolve={handleResolveWithModal}
        isResolving={isResolving}
      />
    </>
  );
}; 